import { Flex } from '@mantine/core';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';

import type { Person } from '@feathr/blackbox';
import { AvatarNameCell, DateTimeCell, NumberCell, Pill, TanStackHeader } from '@feathr/components';
import { TimeFormat } from '@feathr/hooks';
import growthCreditIcon from '@feathr/report_components/images/growth_credit_icon.svg';

import { EDonationEngagementFlavor } from './DonationFormInteractionSelect';

import * as styles from './donationFormEngagementColumns.css';

interface IFirstFlavorHit {
  d_c?: string;
  donation?: { amount?: number; currency?: string; frequency?: string };
  growth_credits?: number;
}

function getFirstFlavorHit(person: Person): IFirstFlavorHit | undefined {
  const innerHits = person.get('inner_hits') as { first_flavor?: IFirstFlavorHit[] } | undefined;
  return innerHits?.first_flavor?.[0];
}

function donationFormEngagementColumns(
  t: TFunction,
  localUrl: ((url: string) => string) | undefined,
  flavor: EDonationEngagementFlavor,
): Array<ColumnDef<Person>> {
  const nameColumn: ColumnDef<Person> = {
    id: 'name',
    accessorFn: (person): string => person.get('name') ?? '',
    header: () => <TanStackHeader title={t('Name')} />,
    // Names are the variable-length content here, so they take the leftover width.
    meta: { isFlex: true },
    enableSorting: true,
    sortDescFirst: false,
    cell: ({ row }: CellContext<Person, unknown>): JSX.Element => (
      <Observer>
        {function useAnonymousFunction(): JSX.Element {
          const person = row.original;
          const name = person.get('name');
          const primary = name?.trim() ? name : t('Anonymous');
          return (
            <AvatarNameCell
              avatarName={name ?? ''}
              avatarPlaceholder={person.get('placeholder')}
              primary={primary}
              secondary={person.get('external_id')}
              suffix={
                person.isArchived ? (
                  <Pill showPrefix={false} variant={'secondary'}>
                    {t('Archived')}
                  </Pill>
                ) : undefined
              }
              to={localUrl ? localUrl(`/data/people/${person.id}`) : undefined}
            />
          );
        }}
      </Observer>
    ),
  };

  const donationColumn: ColumnDef<Person> = {
    id: 'donation',
    header: () => <TanStackHeader title={t('Donation')} />,
    enableSorting: false,
    meta: { align: 'right' },
    cell: ({ row }: CellContext<Person, unknown>): JSX.Element => (
      <Observer>
        {function useAnonymousFunction(): JSX.Element {
          const amount = getFirstFlavorHit(row.original)?.donation?.amount;
          return <NumberCell emptyText={t('N/A')} format={'currency'} value={amount} />;
        }}
      </Observer>
    ),
  };

  const growthCreditsColumn: ColumnDef<Person> = {
    id: 'growth_credits',
    header: () => <TanStackHeader title={t('Growth Credits')} />,
    enableSorting: false,
    meta: { align: 'right' },
    cell: ({ row }: CellContext<Person, unknown>): JSX.Element => (
      <Observer>
        {function useAnonymousFunction(): JSX.Element {
          const credits = getFirstFlavorHit(row.original)?.growth_credits;
          if (typeof credits !== 'number') {
            return <>{t('N/A')}</>;
          }
          return (
            <Flex align={'center'} className={styles.growthCreditsCell}>
              {numeral(credits).format('0,0')}
              <img alt={t('Growth credit icon')} src={growthCreditIcon} />
            </Flex>
          );
        }}
      </Observer>
    ),
  };

  const interactionDateColumn: ColumnDef<Person> = {
    id: 'first_interacted',
    header: () => <TanStackHeader title={t('First interacted')} />,
    enableSorting: false,
    cell: ({ row }: CellContext<Person, unknown>): JSX.Element => (
      <Observer>
        {function useAnonymousFunction(): JSX.Element {
          return (
            <DateTimeCell
              emptyText={'—'}
              format={TimeFormat.pickerShortDateTimeZone}
              value={getFirstFlavorHit(row.original)?.d_c}
            />
          );
        }}
      </Observer>
    ),
  };

  // Viewed and ViewedNoDonation are sourced from form_view breadcrumbs, which never carry
  // donation/growth_credits data.
  if (
    flavor === EDonationEngagementFlavor.Viewed ||
    flavor === EDonationEngagementFlavor.ViewedNoDonation
  ) {
    return [nameColumn, interactionDateColumn];
  }

  return [nameColumn, donationColumn, growthCreditsColumn, interactionDateColumn];
}

export default donationFormEngagementColumns;
